/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import { useKeystoneSession } from '../../auth/use-keystone-session';
import { ComponentData, ComponentItemData } from '@rta/rta-blueprint-library';
import { useEffect, useState } from 'react';

import { NavType } from '@state';
import { getProducts } from '@external';

// Singleton state outside the component
let componentItemDataCache: ComponentItemData | null = null;
let loadPromise: Promise<ComponentItemData | null> | null = null;

// 28s of total backoff -- roughly 6x the measured ~4.6s API cold start
const MAX_LOAD_ATTEMPTS = 8;
const RETRY_BASE_DELAY_MS = 1000;

export const useLoadComponentData = (): ComponentItemData | null => {
	const { getToken, user, status } = useKeystoneSession();
	const isAuthenticated = status === 'ready';

	const [componentItemData, setComponentItemData] = useState<ComponentItemData | null>(componentItemDataCache);

	useEffect(() => {
		if (!isAuthenticated) return;

		if (componentItemDataCache) {
			return;
		}

		if (loadPromise) {
			loadPromise.then(setComponentItemData);
			return;
		}

		loadPromise = (async () => {
			for (let attempt = 1; attempt <= MAX_LOAD_ATTEMPTS; attempt++) {
				try {
					const componentData = await getProducts<Record<string, ComponentData>>({
						fileName: 'component-item-data',
						getToken,
						setError: (error) => console.error('Error loading components:', error)
					});

					// never seed the singleton with an empty catalog -- getInstance ignores options once created, so it stays poisoned for the page's lifetime
					if (componentData && Object.keys(componentData).length > 0) {
						componentItemDataCache = ComponentItemData.getInstance({
							userEmail: user?.email,
							callback: () => componentData
						});

						console.log('Loaded components:', componentItemDataCache);
						return componentItemDataCache;
					}

					console.warn(`Component data was empty (attempt ${attempt}/${MAX_LOAD_ATTEMPTS})`);
				} catch (error) {
					console.error(`Failed to load component data (attempt ${attempt}/${MAX_LOAD_ATTEMPTS}):`, error);
				}

				if (attempt < MAX_LOAD_ATTEMPTS) {
					await new Promise((resolve) => setTimeout(resolve, RETRY_BASE_DELAY_MS * attempt));
				}
			}

			// leave nothing cached so a later mount can retry instead of the app being permanently catalog-less
			loadPromise = null;
			return null;
		})();

		loadPromise.then(setComponentItemData);
	}, [isAuthenticated]);

	return componentItemData;
};
